/**
 * EventBus — Simple publish/subscribe for intra-tool communication.
 *
 * Components should never import other components directly.
 * They communicate via this EventBus using well-known event names.
 */

export type EventHandler<T = unknown> = (payload: T) => void;
export type Unsubscribe = () => void;

export class EventBus {
  private readonly listeners = new Map<string, Set<EventHandler>>();

  on<T = unknown>(event: string, handler: EventHandler<T>): Unsubscribe {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(handler as EventHandler);
    return () => this.off(event, handler);
  }

  off<T = unknown>(event: string, handler: EventHandler<T>): void {
    this.listeners.get(event)?.delete(handler as EventHandler);
  }

  emit<T = unknown>(event: string, payload: T): void {
    const handlers = this.listeners.get(event);
    if (!handlers) return;
    handlers.forEach((h) => {
      try {
        h(payload);
      } catch (err) {
        console.error(`[EventBus] handler error for ${event}:`, err);
      }
    });
  }

  clear(): void {
    this.listeners.clear();
  }
}
